import { useCallback, useEffect, useMemo, useState } from 'react';
import fontService from '../services/fontService';

const loadedFontLinks = new Set();
const requestCache = new Map();
const injectedClassRules = new Set();

const sanitizeSlug = (family) => (family || 'inter')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '') || 'inter';

const createClassRule = (family, fontClass) => `.${fontClass}{font-family:'${family}',sans-serif;}`;

const ensureClassRule = (family, fontClass) => {
  if (injectedClassRules.has(fontClass)) return;

  let styleTag = document.getElementById('mp-font-class-rules');
  if (!styleTag) {
    styleTag = document.createElement('style');
    styleTag.id = 'mp-font-class-rules';
    document.head.appendChild(styleTag);
  }

  styleTag.appendChild(document.createTextNode(createClassRule(family, fontClass)));
  injectedClassRules.add(fontClass);
};

export default function useFonts({ initialFamily = 'Inter', initialWeight = '400' } = {}) {
  const [fonts, setFonts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFont, setSelectedFont] = useState(initialFamily);
  const [selectedWeight, setSelectedWeight] = useState(String(initialWeight));
  const [selectedPreviewUrl, setSelectedPreviewUrl] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState('');

  const loadFontCSS = useCallback((previewUrl, family) => {
    if (!previewUrl) return;

    const fontKey = `${family}::${previewUrl}`;
    if (!loadedFontLinks.has(fontKey)) {
      const link = document.createElement('link');
      link.href = previewUrl;
      link.rel = 'stylesheet';
      link.dataset.mpFont = fontKey;
      document.head.appendChild(link);
      loadedFontLinks.add(fontKey);
    }

    const fontClass = `font-${sanitizeSlug(family)}`;
    ensureClassRule(family, fontClass);
  }, []);

  const fetchWithCache = useCallback(async (key, handler) => {
    if (requestCache.has(key)) {
      return requestCache.get(key);
    }

    const result = await handler();
    requestCache.set(key, result);
    return result;
  }, []);

  const getFonts = useCallback(async ({ queryText = '', categoryValue = '' } = {}) => {
    setLoading(true);
    setError('');

    try {
      const cacheKey = `${queryText.trim()}::${categoryValue}`;

      const endpointData = await fetchWithCache(cacheKey, async () => {
        if (queryText.trim()) {
          return fontService.search(queryText, { category: categoryValue, per_page: 120 });
        }

        return fontService.list({ category: categoryValue, per_page: 120, sort: 'popularity' });
      });

      if (!endpointData?.success) {
        setError(endpointData?.message || 'Não foi possível carregar fontes');
        setFonts([]);
        return;
      }

      const nextFonts = Array.isArray(endpointData?.fonts) ? endpointData.fonts : [];

      setFonts(nextFonts);

      if (!selectedPreviewUrl && nextFonts[0]?.preview_url && nextFonts[0]?.family) {
        setSelectedPreviewUrl(nextFonts[0].preview_url);
        setSelectedFont(nextFonts[0].family);
        loadFontCSS(nextFonts[0].preview_url, nextFonts[0].family);
      }
    } catch {
      setError('Não foi possível carregar fontes');
    } finally {
      setLoading(false);
    }
  }, [fetchWithCache, loadFontCSS, selectedPreviewUrl]);

  useEffect(() => {
    getFonts({ queryText: '', categoryValue: '' });
  }, [getFonts]);

  const listFonts = useCallback(async (params = {}) => {
    const queryText = params.queryText ?? searchQuery;
    const categoryValue = params.categoryValue ?? category;
    await getFonts({ queryText, categoryValue });
  }, [category, getFonts, searchQuery]);

  const searchFonts = useCallback(async (query) => {
    setSearchQuery(query);
    await getFonts({ queryText: query, categoryValue: category });
  }, [category, getFonts]);

  const selectFontFamily = useCallback((font, weight) => {
    const family = font?.family || 'Inter';
    const nextWeight = String(weight || '400');
    const previewUrl = font?.preview_url || '';

    setSelectedFont(family);
    setSelectedWeight(nextWeight);
    setSelectedPreviewUrl(previewUrl);
    loadFontCSS(previewUrl, family);
  }, [loadFontCSS]);

  const selectedFontData = useMemo(() => {
    const fontClass = `font-${sanitizeSlug(selectedFont)}`;
    return {
      family: selectedFont,
      weight: selectedWeight,
      preview_url: selectedPreviewUrl,
      font_class: fontClass,
    };
  }, [selectedFont, selectedWeight, selectedPreviewUrl]);

  const ensureSelectedLoaded = useCallback(() => {
    if (selectedPreviewUrl && selectedFont) {
      loadFontCSS(selectedPreviewUrl, selectedFont);
    }
  }, [loadFontCSS, selectedFont, selectedPreviewUrl]);

  return {
    fonts,
    loading,
    error,
    selectedFont,
    selectedWeight,
    selectedFontData,
    setSelectedWeight,
    searchQuery,
    category,
    setCategory,
    listFonts,
    searchFonts,
    loadFontCSS,
    selectFontFamily,
    ensureSelectedLoaded,
  };
}
